
import React, { PureComponent } from 'react';

import { translate } from '../../../base/i18n';
import { Icon, IconInfo, IconWarning } from '../../../base/icons';
import { getAllFakeParticipantsLength } from '../../../base/participants';
import { connect } from '../../../base/redux';
import Tooltip from '../../../base/tooltip/components/Tooltip';
import { setTileLimitTileView } from '../../actions.web';

/**
 * Allowed discrete values for the range slider.
 */
const ALLOWED_VALUES = [ 5, 7, 9, 14, 17, 20, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40 ];

/**
 * The type of the React {@code Component} props of {@link TileRangeSlider}.
 */
type Props = {

    /**
     * The current tile view tile limit from Redux state.
     */
    _tileViewTileLimit: number,

    /**
     * The translation function.
     */
    t: Function,

    /**
     * The dispatch function from Redux.
     */
    dispatch: Function,

    /**
     * The callback to invoke when the range slider value changes.
     */
    onChange?: Function,

    /**
     * Whether to show the counter/value display.
     */
    showCounter?: boolean,

    /**
     * The label for the range slider.
     */
    label?: string
};

/**
 * The type of the React {@code Component} state of {@link TileRangeSlider}.
 */
type State = {

    /**
     * The current index position in the ALLOWED_VALUES array.
     */
    sliderIndex: number
};

/**
 * Implements a React {@link Component} which displays an input slider for
 * adjusting values with visual feedback, restricted to discrete allowed values.
 *
 * @augments Component
 */
class TileRangeSlider extends PureComponent<Props, State> {
    /**
     * Initializes a new {@code TileRangeSlider} instance.
     *
     * @param {Object} props - The read-only properties with which the new
     * instance is to be initialized.
     */
    constructor(props: Props) {
        super(props);
        const allowedValues = this._getAllowedValues();
        const initialIndex = allowedValues.indexOf(this.props._tileViewTileLimit || 7);

        this.state = {
            sliderIndex: initialIndex >= 0 ? initialIndex : 0
        };

        // Bind event handlers so they are only bound once for every instance.
        this._onRangeChange = this._onRangeChange.bind(this);
    }

    /**
     * Updates component state when props change.
     *
     * @param {Props} nextProps - The new props.
     * @returns {void}
     */
    componentDidUpdate(prevProps: Props) {
        const allowedValues = this._getAllowedValues();

        // Sync state with Redux store if the value changed externally
        if (prevProps._tileViewTileLimit !== this.props._tileViewTileLimit) {
            const newIndex = allowedValues.indexOf(this.props._tileViewTileLimit);

            if (newIndex >= 0 && newIndex !== this.state.sliderIndex) {
                this.setState({
                    sliderIndex: newIndex
                });
            }
        }
    }

    /**
     * Updates the range slider's value when it changes.
     *
     * @param {Object} event - The change event from the slider input.
     * @private
     * @returns {void}
     */
    _onRangeChange(event) {
        const newIndex = Number(event.currentTarget.value);
        const allowedValues = this._getAllowedValues();
        const newValue = allowedValues[newIndex];

        this.setState({
            sliderIndex: newIndex
        });

        // Dispatch to Redux store using props.dispatch instead of APP.store
        this.props.dispatch(setTileLimitTileView(newValue));

        // Invoke the onChange callback with the actual value
        if (this.props.onChange) {
            this.props.onChange(newValue);
        }
    }

    /**
     * Returns the filtered allowed values based on fakeParticipantsLength.
     * If fakeParticipantsLength > 4, excludes 5 and starts from 7.
     *
     * @private
     * @returns {Array<number>} The filtered allowed values.
     */
    _getAllowedValues() {
        const { fakeParticipantsLength } = this.props;

        if (fakeParticipantsLength > 4) {
            return ALLOWED_VALUES.filter(value => value !== 5);
        }

        return ALLOWED_VALUES;
    }

    /**
     * Calculates the fill percentage for the range slider track.
     * Adjusts for thumb width to prevent visual overlap.
     *
     * @private
     * @returns {string} The calculated width for the fill.
     */
    _getTrackFillWidth() {
        const { sliderIndex } = this.state;
        const allowedValues = this._getAllowedValues();
        const maxIndex = allowedValues.length - 1;

        if (maxIndex === 0) {
            return '0%';
        }

        const percentage = (sliderIndex / maxIndex) * 100;

        // Adjust for thumb width (approximately 16px)
        // Subtract a proportional amount based on position to prevent overflow
        const thumbWidth = 16;
        const adjustment = (percentage / 100) * thumbWidth;

        return `calc(${percentage}% - ${adjustment}px)`;
    }

    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement}
     */
    render() {
        const {
            showCounter = true,
            label,
            t
        } = this.props;
        const { sliderIndex } = this.state;
        const allowedValues = this._getAllowedValues();
        const currentValue = allowedValues[sliderIndex];
        const maxIndex = allowedValues.length - 1;
        const fillWidth = this._getTrackFillWidth();

        // Inline styles
        const styles = {
            tileRangeItem: {
                listStyle: 'none',
                padding: 0,
                margin: 0,
                width: '90%'
            },
            tileRangeContents: {
            },
            tileRangeLabel: {
                display: 'block',
                fontWeight: 'bold',
                marginBottom: '0.5em',
                color: '#17181c'
            },
            tileRangeWrapper: {
                display: 'flex',
                alignItems: 'center',
                width: '100%'
            },
            tileRangeIcon: {
                display: 'flex',
                alignItems: 'center',
                marginRight: '0.75em',
                flexShrink: 0
            },
            tileRangeInputContainer: {
                display: 'block',
                position: 'relative',
                marginRight: '0.375em',
                flex: 1
            },
            tileRangeSlider: {
                WebkitAppearance: 'none',
                appearance: 'none',
                backgroundColor: '#fff',
                display: 'block',
                margin: '0.5em 0',
                padding: 0,
                width: '100%',
                border: 0,
                boxSizing: 'border-box',
                borderRadius: '0.25em',
                height: '0.5em',
                outline: 'none'
            },
            tileRangeInputFill: {
                backgroundColor: '#5583f6',
                pointerEvents: 'none',
                position: 'absolute',
                left: 0,
                top: 'calc(50% - 0.25em)',
                borderRadius: '0.25em',
                height: '0.5em',
                transition: 'width 0.1s linear'
            },
            tileRangeCounter: {
                height: '1.5em',
                margin: 'auto 0',
                overflow: 'hidden',
                textAlign: 'center',
                position: 'relative',
                marginBottom: '5px',
                marginLeft: '5px'
            },
            tileRangeCounterValue: {
                display: 'flex',
                fontSize: '14px',
                fontWeight: '500',
                color: '#ffffff'
            }
        };


        return (
            <>
                <div className = 'tile-view-child tile-view-parent'>
                    <div className = 'tile-view-child'>
                        <p>{t('toolbar.participantLimit')}</p>
                        <Tooltip
                            content = { <div className = 'tile-view-tooltip'>
                                <span>{t('toolbar.helptip')}</span>
                                <span>{t('toolbar.sortingorder')}</span>
                                <div className = 'tile-view-tooltip-warning'>
                                    <div className = 'tile-view-tooltip-warning-icon'><Icon src = { IconWarning } /><span>{t('toolbar.caution')}</span></div>
                                    <span>{t('toolbar.sortingorderwarning')}</span>
                                </div>
                            </div> }
                            position = 'top tile-sorting-helptip'>
                            <Icon src = { IconInfo } />
                        </Tooltip>
                    </div>
                </div>
                <li
                    className = 'tile-range-slider-item'
                    style = { styles.tileRangeItem }>
                    <div style = { styles.tileRangeContents }>
                        {label && (
                            <label style = { styles.tileRangeLabel }>
                                {label}
                            </label>
                        )}
                        <div style = { styles.tileRangeWrapper }>
                            <div style = { styles.tileRangeInputContainer }>
                                <input
                                    className = 'tile-range-slider-input'
                                    max = { maxIndex }
                                    min = { 0 }
                                    onChange = { this._onRangeChange }
                                    style = { styles.tileRangeSlider }
                                    type = 'range'
                                    value = { sliderIndex } />
                                <span
                                    style = {{
                                        ...styles.tileRangeInputFill,
                                        width: fillWidth
                                    }} />
                            </div>
                            {showCounter && (
                                <div style = { styles.tileRangeCounter }>
                                    <span style = { styles.tileRangeCounterValue }>
                                        {currentValue}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </li>
            </>
        );
    }
}


/**
 * Maps (parts of) the Redux state to the associated {@code TileRangeSlider}'s props.
 *
 * @param {Object} state - The Redux state.
 * @private
 * @returns {Props}
 */
function _mapStateToProps(state) {
    const { tileViewTileLimit } = state['features/filmstrip'];
    const fakeParticipantsLength = getAllFakeParticipantsLength(state);

    return {
        _tileViewTileLimit: tileViewTileLimit,
        fakeParticipantsLength
    };
}
export default translate(connect(_mapStateToProps)(TileRangeSlider));
